import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { UserCircleIcon, BuildingOfficeIcon, PencilSquareIcon, CheckIcon, XMarkIcon, KeyIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { db, auth } from '../../lib/firebase';
import CommonPageHeader from '../common/CommonPageHeader';

interface UserProfile {
  displayName: string;
  email: string;
  photoURL?: string;
  phone?: string;
}

interface Organization {
  name: string;
  taxNumber: string;
  registrationNumber: string;
  isActive: boolean;
}

interface PasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [profile, setProfile] = useState<UserProfile>({
    displayName: user?.displayName || '',
    email: user?.email || '',
    photoURL: user?.photoURL,
    phone: ''
  });
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [passwordData, setPasswordData] = useState<PasswordData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Ellenőrizzük, hogy Google-lal jelentkezett-e be
  const isGoogleUser = user?.providerData?.some(provider => provider.providerId === 'google.com');

  useEffect(() => {
    loadUserData();
  }, [user?.id]);

  const handleProfileSave = async () => {
    if (!user?.id) return;

    try {
      await updateDoc(doc(db, 'users', user.id), {
        phone: profile.phone
      });

      toast.success(t('common.messages.profile.updateSuccess'));
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('common.messages.profile.updateError'));
    }
  };

  const handleCancel = () => {
    // Reset phone field to original value
    if (user?.id) {
      loadUserData();
    }
    setIsEditing(false);
  };

  const handlePasswordChange = async () => {
    if (!auth.currentUser) return;

    // Validáció
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t('common.messages.profile.passwordMismatch'));
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error(t('common.messages.profile.passwordTooShort'));
      return;
    }

    try {
      // Re-authentication szükséges a jelszó módosításhoz
      const credential = EmailAuthProvider.credential(
        auth.currentUser.email!,
        passwordData.currentPassword
      );
      
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, passwordData.newPassword);

      toast.success(t('common.messages.profile.passwordChangeSuccess'));
      setIsChangingPassword(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error: any) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        toast.error(t('common.messages.profile.wrongPassword'));
      } else if (error.code === 'auth/weak-password') {
        toast.error(t('common.messages.profile.weakPassword'));
      } else {
        toast.error(t('common.messages.profile.passwordChangeError'));
      }
    }
  };

  const handlePasswordCancel = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setIsChangingPassword(false);
  };

  const loadUserData = async () => {
    if (!user?.id) return;

    try {
      const userDoc = await getDoc(doc(db, 'users', user.id));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setProfile(prev => ({
          ...prev,
          phone: userData.phone || ''
        }));

        // Szervezet adatainak betöltése
        if (userData.organizationId) {
          const orgDoc = await getDoc(doc(db, 'organizations', userData.organizationId));
          if (orgDoc.exists()) {
            const orgData = orgDoc.data();
            setOrganization({
              name: orgData.name,
              taxNumber: orgData.taxNumber,
              registrationNumber: orgData.registrationNumber,
              isActive: orgData.isActive
            });
          }
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  return (
    <>
      <CommonPageHeader
        title={t('common.profile.title')}
        subtitle={t('common.profile.subtitle')}
        icon={UserCircleIcon}
      >
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <PencilSquareIcon className="h-4 w-4 mr-2" />
            <span>{t('common.actionsObject.edit')}</span>
          </button>
        ) : (
          <div className="flex items-center space-x-2">
            <button
              onClick={handleProfileSave}
              className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              <CheckIcon className="h-4 w-4 mr-2" />
              <span>{t('common.actionsObject.save')}</span>
            </button>
            <button
              onClick={handleCancel}
              className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              <XMarkIcon className="h-4 w-4 mr-2" />
              <span>{t('common.actionsObject.cancel')}</span>
            </button>
          </div>
        )}
      </CommonPageHeader>

      <div className="space-y-6 pb-20 sm:pb-6">
        {/* Profil adatok szekció */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
            <div className="flex items-center space-x-3">
              {profile.photoURL ? (
                <img
                  src={profile.photoURL}
                  alt={profile.displayName}
                  className="h-8 w-8 rounded-full"
                />
              ) : (
                <UserCircleIcon className="h-8 w-8 text-primary-600" />
              )}
              <h2 className="text-lg font-medium text-gray-800 dark:text-gray-100">{t('common.profile.personalInfo')}</h2>
            </div>
          </div>
          
          <div className="p-6">

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.profile.fullName')}
                </label>
                <input
                  type="text"
                  value={profile.displayName}
                  disabled={true}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 dark:placeholder-gray-400 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.profile.email')}
                </label>
                <input
                  type="email"
                  value={profile.email}
                  disabled={true}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 dark:placeholder-gray-400 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 cursor-not-allowed"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.profile.phone')}
                </label>
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  disabled={!isEditing}
                  placeholder="+36201234567"
                  className={`block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 dark:placeholder-gray-400 text-gray-700 dark:text-gray-300 ${
                    !isEditing ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800'
                  }`}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Szervezet adatok szekció */}
        {organization && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center space-x-3">
                <BuildingOfficeIcon className="h-8 w-8 text-primary-600" />
                <h2 className="text-lg font-medium text-gray-800 dark:text-gray-100">{t('common.profile.organization')}</h2>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('common.profile.organizationName')}
                  </label>
                  <input
                    type="text"
                    value={organization.name}
                    disabled={true}
                    className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 dark:placeholder-gray-400 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('common.profile.taxNumber')}
                  </label>
                  <input
                    type="text"
                    value={organization.taxNumber}
                    disabled={true}
                    className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 dark:placeholder-gray-400 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('common.profile.registrationNumber')}
                  </label>
                  <input
                    type="text"
                    value={organization.registrationNumber}
                    disabled={true}
                    className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 dark:placeholder-gray-400 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('common.profile.organizationStatus')}
                  </label>
                  <div className="mt-2">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${
                      organization.isActive 
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700' 
                        : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700'
                    }`}>
                      {organization.isActive ? t('status.active') : t('status.inactive')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Jelszó módosítás szekció - csak email/jelszó felhasználóknak */}
        {!isGoogleUser && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <KeyIcon className="h-8 w-8 text-primary-600" />
                  <h2 className="text-lg font-medium text-gray-800 dark:text-gray-100">{t('common.profile.changePassword')}</h2>
                </div>
                {!isChangingPassword && (
                  <button
                    onClick={() => setIsChangingPassword(true)}
                    className="px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-700 rounded-md hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors"
                  >
                    <PencilSquareIcon className="h-4 w-4 inline mr-1" />
                    {t('common.actionsObject.edit')}
                  </button>
                )}
              </div>
            </div>
            
            <div className="p-6">
              {!isChangingPassword ? (
                <div className="flex items-center space-x-3 text-gray-600 dark:text-gray-400">
                  <ShieldCheckIcon className="h-5 w-5" />
                  <span className="text-sm">A jelszó biztonságosan tárolva van. Kattintson a szerkesztés gombra a módosításhoz.</span>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('common.profile.currentPassword')}
                    </label>
                    <input
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 dark:placeholder-gray-400 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800"
                      placeholder="Jelenlegi jelszó"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('common.profile.newPassword')}
                    </label>
                    <input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 dark:placeholder-gray-400 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800"
                      placeholder="Új jelszó (min. 6 karakter)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('common.profile.confirmPassword')}
                    </label>
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 dark:placeholder-gray-400 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800"
                      placeholder="Új jelszó megerősítése"
                    />
                  </div>

                  <div className="flex items-center justify-end space-x-3 pt-4">
                    <button
                      onClick={handlePasswordCancel}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <XMarkIcon className="h-4 w-4 inline mr-1" />
                      {t('common.actionsObject.cancel')}
                    </button>
                    <button
                      onClick={handlePasswordChange}
                      disabled={!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                      className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <CheckIcon className="h-4 w-4 inline mr-1" />
                      {t('common.profile.updatePassword')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Google felhasználók számára tájékoztató */}
        {isGoogleUser && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
            <div className="flex items-center space-x-3">
              <ShieldCheckIcon className="h-8 w-8 text-blue-600" />
              <div>
                <h3 className="text-lg font-medium text-blue-900">Google fiók</h3>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  Google fiókkal jelentkezett be. A jelszó módosítása a Google fiók beállításaiban lehetséges.
                </p>
                <a
                  href="https://myaccount.google.com/security"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center mt-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300"
                >
                  Google fiók biztonsági beállítások
                  <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};